import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import { OccupationForm } from '../models/occupation.model';
import { OccupationFormService, OccupationSurface } from '../occupation-form.service';

const KEY_PREFIX = 'visualization.occupation.legend.';

/**
 * Says how to read the chart it sits above.
 *
 * Folded away once folded, because the explanation is only new once — but expanded until
 * then, since a step curve against a dashed capacity rule is not self-evident, and the
 * scope line in particular states something the chart cannot show: that the usage counts
 * routes the reader never selected.
 */
@Component({
  selector: 'app-occupation-legend',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  templateUrl: './occupation-legend.component.html',
  styleUrl: './occupation-legend.component.css'
})
export class OccupationLegendComponent {
  private readonly formService = inject(OccupationFormService);
  protected readonly ICONS = ICONS;

  readonly surface = input.required<OccupationSurface>();
  readonly form = input.required<OccupationForm>();
  /** True where the chart outlines the step it was opened from. */
  readonly marksOpenStep = input<boolean>(false);

  protected readonly collapsed = computed(() =>
    this.formService.legendCollapsedFor(this.surface())()
  );

  protected readonly lines = computed(() => {
    const keys =
      this.form() === 'ladder'
        ? [`${KEY_PREFIX}ladder.lanes`, `${KEY_PREFIX}ladder.overflow`]
        : [`${KEY_PREFIX}area.usage`, `${KEY_PREFIX}area.capacity`];

    keys.push(`${KEY_PREFIX}scope`);
    if (this.marksOpenStep()) keys.push(`${KEY_PREFIX}mark_window`);
    return keys;
  });

  protected toggle(): void {
    this.formService.toggleLegend(this.surface());
  }
}
